
import { defineComponent } from "vue";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Divider from "primevue/divider";
import Calendar from "primevue/calendar";
import IconSearch from "~icons/tabler/search";
import IconFilter from "~icons/tabler/adjustments";
import IconChevronDown from "~icons/tabler/chevron-down";
import IconClose from "~icons/tabler/x";
import IconChevronUp from "~icons/tabler/chevron-up";
import FeedbackCard from "@/components/publicFeedback/FeedbackCard.vue";
import Map from "@/components/Map.vue";
import { Coordinates, Marker } from "@/typings/Marker";
import { FeedbackItem } from "@/typings/FeedbackItem";
import { Paginated } from "@/typings/Paginated";
import { FeedbackType } from "@/typings/FeedbackType";
import { TellCityLocalCommunity } from "@/typings/TellCityLocalCommunity";
import MultiSelect from "primevue/multiselect";

export default defineComponent({
  data: () => ({
    page: 1,
    search: "",
    showFilters: true,
    isLoading: false,
    feedbacks: {
      data: [],
      current_page: 0,
      from: 0,
      last_page: 0,
      per_page: 0,
      to: 0,
      total: 0,
    } as Paginated<FeedbackItem>,
    feedbackTypes: [] as FeedbackType[],
    localCommunities: [] as TellCityLocalCommunity[],
    selectedLocalCommunity: null,
    categories: [],
    selectedCategory: null,
    dateRange: [] as Date[],
    center: {
      lat: parseFloat(process.env.VUE_APP_CITY_CENTER_LATITUDE),
      lng: parseFloat(process.env.VUE_APP_CITY_CENTER_LONGITUDE),
    } as Coordinates,
    zoom: 14,
    statistics: {
      finished: 0,
      inProgress: 0,
    },
  }),
  methods: {
    updateCenter(value: Coordinates) {
      this.center = value;
    },
    updateZoom(value: number) {
      this.zoom = value;
    },
    getFeedbackTypes() {
      this.$http.get("api/v1/feedback/types").then((response) => {
        this.feedbackTypes = response.data.types;
      });
    },
    getFeedbacks() {
      let params = {
        page: this.page,
        perPage: 10,
        orderBy: {
          created_at: "desc",
        },
        public: true,
        statistics: true,
        dateRange: this.dateRange.length ? this.formattedDateRange : undefined,
        filters: {
          title: this.search
            ? encodeURIComponent("%" + this.search.trim() + "%")
            : undefined,
          local_community_id: this.selectedLocalCommunity
            ? this.selectedLocalCommunity
            : undefined,
          type_id: this.selectedCategory ? this.selectedCategory : undefined,
        },
      };
      this.isLoading = true;
      this.$http
        .get("api/v1/public-feedbacks", { params })
        .then((response) => {
          if (this.page !== 1) {
            this.feedbacks.data = this.feedbacks.data.concat(
              response.data.data,
            );
            this.feedbacks.current_page = response.data.current_page;
            return;
          }
          this.feedbacks = response.data;
          this.statistics = response.data.statistics;
        })
        .finally(() => (this.isLoading = false));
    },
    getLocalCommunities() {
      this.$http.get("api/v1/feedback/local-communities").then((response) => {
        this.localCommunities = [
          ...response.data,
          { id: 9999, name: this.$t("feedback.all_local_communities") }, // Option for all local communities since null is not allowed
        ];
      });
    },
    loadMore() {
      this.page++;
      this.getFeedbacks();
    },
    selectDateRange() {
      if (this.dateRange.some((date) => !date)) {
        return;
      }

      this.getFeedbacks();
    },
    clearDateRange() {
      this.dateRange = [];
      this.getFeedbacks();
    },
  },
  computed: {
    showLoadMore() {
      return this.feedbacks && this.feedbacks.last_page > this.page;
    },
    markers() {
      if (!this.feedbacks) {
        return [];
      }

      return this.feedbacks.data
        .filter(
          (feedback) =>
            feedback.latitude !== null &&
            feedback.longitude !== null &&
            !isNaN(Number(feedback.latitude)) &&
            !isNaN(Number(feedback.longitude)),
        )
        .map((feedback) => ({
          id: feedback.id,
          position: {
            lat: Number(feedback.latitude),
            lng: Number(feedback.longitude),
          },
          icon: "/markers/check-marker.png",
          on_click_route_name: "PublicFeedbackById",
        })) as Array<Marker>;
    },

    types() {
      return this.feedbackTypes.map((type) => {
        return {
          id: type.id,
          name: this.$t(`feedback.${type.name}`),
        };
      });
    },
    statisticsPercentage() {
      if (this.statistics.finished + this.statistics.inProgress === 0) {
        return 0;
      }

      return Math.round(
        (this.statistics.finished /
          (this.statistics.finished + this.statistics.inProgress)) *
          100,
      );
    },
    formattedDateRange() {
      if (!this.dateRange || !this.dateRange.length) {
        return [];
      }

      return this.dateRange.map((date) => {
        if (!date) {
          return null;
        }

        return this.$moment(date).format("DD-MM-YYYY");
      });
    },
  },
  mounted() {
    this.getFeedbacks();
    this.getLocalCommunities();
    this.getFeedbackTypes();
  },
  components: {
    Button,
    InputText,
    Divider,
    IconSearch,
    IconFilter,
    IconChevronDown,
    IconChevronUp,
    IconClose,
    FeedbackCard,
    Calendar,
    Map,
    MultiSelect,
  },
});
